import React, { Fragment } from "react";
import {
  buildQuoteEstimateGroups,
  computeQuoteEstimateTotals,
} from "./quoteEstimatePrintHelpers";

// Inline styles so the layout renders identically through html2pdf/html2canvas
// without depending on app CSS. Colors approximate the estimate PDF:
// gray category headings, peach Base Bid, light-blue Total.
const S = {
  wrap: { fontFamily: "Arial, Helvetica, sans-serif", color: "#222", padding: "8px" },
  header: { textAlign: "center", margin: "0 0 12px" },
  logo: { maxHeight: "90px", maxWidth: "260px", objectFit: "contain", margin: "0 auto 4px", display: "block" },
  preparedFor: { fontSize: "22px", fontWeight: 700, margin: "4px 0 0" },
  property: { fontSize: "16px", fontWeight: 600, margin: "2px 0 0" },
  validity: { fontSize: "12px", margin: "2px 0 8px" },
  planImage: { maxWidth: "260px", maxHeight: "220px", objectFit: "contain", border: "1px solid #ccc", display: "block", margin: "0 auto 4px" },
  footer: { marginTop: "16px", fontSize: "12px", lineHeight: 1.5 },
  title: { textAlign: "center", fontSize: "20px", fontWeight: 700, margin: "0 0 8px" },
  // tableLayout: fixed + an explicit colgroup keeps the columns aligned in the
  // html2pdf/html2canvas render (auto layout distributes column widths
  // inconsistently on the canvas and shifts the headers off their columns).
  table: { width: "100%", borderCollapse: "collapse", fontSize: "13px", tableLayout: "fixed" },
  thLeft: { textAlign: "left", borderBottom: "1px solid #ccc", padding: "8px", fontWeight: 600 },
  thRight: { textAlign: "right", borderBottom: "1px solid #ccc", padding: "8px", fontWeight: 600 },
  catHead: { background: "#efefef", fontWeight: 700, fontSize: "15px", padding: "8px", borderTop: "1px solid #ddd", textAlign: "left" },
  tdLeft: { textAlign: "left", padding: "8px", borderBottom: "1px solid #eee", wordBreak: "break-word" },
  tdRight: { textAlign: "right", padding: "8px", borderBottom: "1px solid #eee" },
  baseBidLabel: { background: "#fce3c0", fontWeight: 700, padding: "8px", textAlign: "left" },
  baseBidValue: { background: "#fce3c0", fontWeight: 700, padding: "8px", textAlign: "right" },
  totalLabel: { background: "#dbe9f5", fontWeight: 700, padding: "8px", textAlign: "right" },
  totalValue: { background: "#dbe9f5", fontWeight: 700, padding: "8px", textAlign: "right" },
  detailsTable: { width: "420px", maxWidth: "70%", borderCollapse: "collapse", fontSize: "13px", margin: "0 auto 8px" },
  detailLabel: { textAlign: "left", padding: "4px 8px", fontWeight: 700, width: "40%", verticalAlign: "top", background: "#f7f7f7", border: "1px solid #e5e5e5" },
  detailValue: { textAlign: "left", padding: "4px 8px", border: "1px solid #e5e5e5", verticalAlign: "top" },
  instrWrap: { width: "420px", maxWidth: "70%", margin: "0 auto 8px", border: "1px solid #e5e5e5" },
  instrHead: { background: "#f7f7f7", fontWeight: 700, fontSize: "13px", padding: "5px 8px", borderBottom: "1px solid #e5e5e5" },
  instrBody: { padding: "6px 8px", fontSize: "13px", whiteSpace: "pre-wrap", lineHeight: 1.4 },
};

const QuoteEstimatePrint = ({
  materialListData,
  laborData,
  unitName,
  unitTypeName,
  quoteNumber,
  purchaseOrder,
  quoteDate,
  taxAmount,
  customerInstructions,
  storeLogo,
  propertyName,
  planImage,
  handleFormat,
  ImageContainer
}) => {
  const toMoney = (n) =>
    (parseFloat(n) || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  // Reuse OrderConfirmation's handleFormat for real values (comma + 2 decimals).
  // It returns the number 0 for falsy input, so normalize that to "0.00" — the
  // estimate shows "$0.00" for zero line totals, never "$0".
  const fmt = (n) => {
    const out = typeof handleFormat === "function" ? handleFormat(n) : toMoney(n);
    return typeof out === "number" ? toMoney(out) : out;
  };

  const groups = buildQuoteEstimateGroups({ materialListData, laborData });
  const { baseBid, total, lastPrimaryName } = computeQuoteEstimateTotals(groups);
  // Tax comes from the quote's calculated TotalTax; the grand Total is the line-item
  // subtotal plus tax.
  const tax = parseFloat(taxAmount) || 0;
  const grandTotal = (parseFloat(total) || 0) + tax;
  return (
    <div style={S.wrap}>
      <div style={S.header}>
        {storeLogo ? <img src={storeLogo} id="quote-store-logo" alt="Store Logo" style={S.logo} /> : null}
        <h1 style={S.preparedFor}>Estimate Prepared For</h1>
        {propertyName ? <h3 style={S.property}>{propertyName}</h3> : null}
        <p style={S.validity}>Estimates valid for 30 days</p>
        {planImage ? <img src={planImage} id="quote-floorplan" alt="Floor Plan" style={S.planImage} /> : null}
          
      </div>
      <h3 style={S.title}>
        Estimate for {unitName} ({unitTypeName})
      </h3>
      {(() => {
        const details = [
          ["Quote #", quoteNumber],
          ["Unit #", unitName],
          ["Unit Type", unitTypeName],
          ["PO #", purchaseOrder],
          ["Date", quoteDate],
          ["Tax", `$${fmt(tax)}`],
          ["Total", `$${fmt(grandTotal)}`],
        ].filter(([, v]) => v != null && String(v).trim() !== "");
        if (details.length === 0) return null;
        return (
          <table style={S.detailsTable}>
            <tbody>
              {details.map(([label, value]) => (
                <tr key={label}>
                  <td style={S.detailLabel}>{label}</td>
                  <td style={S.detailValue}>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      })()}
      {customerInstructions != null && String(customerInstructions).trim() !== "" && (
        <div style={S.instrWrap}>
          <div style={S.instrHead}>Customer Instructions</div>
          <div style={S.instrBody}>{customerInstructions}</div>
        </div>
      )}
      <table style={S.table}>
        {/* Estimate shows only the line item and its line total — QTY and Price
            columns are intentionally omitted for the client-facing quote. */}
        <colgroup>
          <col style={{ width: "78%" }} />
          <col style={{ width: "22%" }} />
        </colgroup>
        <thead>
          <tr>
            <th style={S.thLeft}>Line Item</th>
            <th style={S.thRight}>Line Total</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <Fragment key={g.name}>
              <tr>
                <td style={S.catHead} colSpan={2}>
                  {g.name}
                </td>
              </tr>
              {g.items.map((it, i) => (
                <tr key={i}>
                  <td style={S.tdLeft}>{it.name}</td>
                  <td style={S.tdRight}>${fmt(it.lineTotal)}</td>
                </tr>
              ))}
              {lastPrimaryName != null && g.name === lastPrimaryName && (
                <tr>
                  <td style={S.baseBidLabel}>Base Bid</td>
                  <td style={S.baseBidValue}>${fmt(baseBid)}</td>
                </tr>
              )}
            </Fragment>
          ))}
          <tr>
            <td style={S.totalLabel}>Total</td>
            <td style={S.totalValue}>${fmt(total)}</td>
          </tr>
        </tbody>
      </table>
      {/* pageBreak class matches handlePrintQuote's html2pdf pagebreak.before selector */}
      <div className="pageBreak" style={S.footer}>
        <p>
          * All pricing is subject to labor minimum charges and/or trip charges.
          Pricing is good for 30 days from receipt and subject to change.
        </p>
        <p>
          Change Orders:&nbsp;&nbsp;&nbsp;Price includes standard floor preparation, defined as
          light skimcoating of hairline cracks or small recesses (less than
          2&quot; in diameter and 1/2&quot; in depth). Anything in excess
          is atypical and will incur additional floor prep costs.&nbsp;&nbsp;&nbsp;All
          changes/additional costs will be quoted for approval prior to
          commencement of additional work.&nbsp;&nbsp;&nbsp;Pricing does not include moving
          furniture, toilets, appliances, or modifications to any other unit
          finishes, electronics, or plumbing.&nbsp;&nbsp;&nbsp;Toilet or appliance moving
          must be requested before day of install and is limited to specific
          items.&nbsp;&nbsp;&nbsp;Warranty:&nbsp;&nbsp;Standard Interiors warrants all labor for a period
          of one year.&nbsp;&nbsp;&nbsp;Materials are warranted by the manufacturer.
        </p>
      </div>
    </div>
  );
};

export default QuoteEstimatePrint;
